/**
 * Shared API client with credentials included
 * All requests will send authentication cookies
 */

import axios, { type AxiosResponse } from 'axios';
import config from '../config';
import type { Venue, Event, EventFormData } from '../types';
import { getClerkToken } from './authToken';

const api = axios.create({
  baseURL: config.apiBaseUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach the Clerk session token when signed in, so the backend can
// authenticate via Clerk (clerkAuth). The legacy JWT cookie still rides along
// (withCredentials) for routes not yet cut over. window.Clerk is set globally
// by ClerkProvider.
api.interceptors.request.use(async (req) => {
  const token = await getClerkToken();
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.error || error.message || 'An error occurred';

    // You can add global error handling here
    console.error('API Error:', message);

    return Promise.reject(error);
  }
);

// Helper functions for common operations
export const fetchVenues = (): Promise<AxiosResponse<Venue[]>> => api.get('/venues');
interface EventsApiResponse {
  status: string;
  data: { events: Event[] };
}
export const fetchEvents = (includeArchived = false): Promise<AxiosResponse<EventsApiResponse>> =>
  api.get(`/events${includeArchived ? '?archived=true' : ''}`);
export const createEvent = (data: EventFormData): Promise<AxiosResponse<Event>> =>
  api.post('/events', data);
export const updateEvent = (id: number, data: EventFormData): Promise<AxiosResponse<Event>> =>
  api.put(`/events/${id}`, data);
export const archiveEvent = (id: number, archived: boolean): Promise<AxiosResponse<Event>> =>
  api.patch(`/events/${id}/archive`, { archived });
export const deleteEvent = (id: number): Promise<AxiosResponse<void>> =>
  api.delete(`/events/${id}`);
export const createVenue = (formData: FormData): Promise<AxiosResponse<Venue>> =>
  api.post('/venues', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const updateVenue = (id: number, formData: FormData): Promise<AxiosResponse<Venue>> =>
  api.put(`/venues/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const deleteVenue = (id: number): Promise<AxiosResponse<void>> =>
  api.delete(`/venues/${id}`);

export default api;
